import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  TextField,
  Divider,
  IconButton,
  LinearProgress,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  Edit as EditIcon,
  PictureAsPdf as PdfIcon,
  SmartToy as AIIcon,
} from '@mui/icons-material';
import { AppDispatch, RootState } from '../../store/store';
import { processInvoiceWithAI, createInvoiceFromAI, clearAIProcessing } from '../../store/slices/invoiceSlice';
import { InvoiceAIData, InvoiceAIItem } from '../../types/aiInvoice';

interface AIInvoiceUploadProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const AIInvoiceUpload: React.FC<AIInvoiceUploadProps> = ({ open, onClose, onSuccess }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { aiProcessing } = useSelector((state: RootState) => state.invoices);

  // Batch upload state
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [batchMode, setBatchMode] = useState(false);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [batchResults, setBatchResults] = useState<Map<string, {
    status: 'pending' | 'processing' | 'completed' | 'error';
    data: InvoiceAIData | null;
    error: string | null;
    pdfUrl: string | null;
  }>>(new Map());
  
  // Single upload state (legacy)
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [editedData, setEditedData] = useState<InvoiceAIData | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [processingTime, setProcessingTime] = useState(0);

  const steps = ['Nahrát PDF', 'AI Zpracování', 'Kontrola Dat', 'Vytvořit Fakturu'];

  // Update edited data when extracted data changes (only in single file mode)
  useEffect(() => {
    if (aiProcessing.extractedData && !batchMode) {
      setEditedData({ ...aiProcessing.extractedData });
      setActiveStep(2); // Move to review step
    }
  }, [aiProcessing.extractedData, batchMode]);

  // Handle file selection (single or multiple)
  const handleFileSelect = (files: FileList | File | null) => {
    if (!files) return;
    
    const fileArray = files instanceof FileList ? Array.from(files) : [files];
    const pdfFiles = fileArray.filter(file => file.type === 'application/pdf');
    
    if (pdfFiles.length === 0) {
      alert('Prosím vyberte PDF soubor(y)');
      return;
    }
    
    if (pdfFiles.length !== fileArray.length) {
      alert(`${fileArray.length - pdfFiles.length} soubor(ů) bylo přeskočeno (pouze PDF jsou podporovány)`);
    }
    
    if (pdfFiles.length === 1) {
      // Single file mode
      setBatchMode(false);
      setUploadedFile(pdfFiles[0]);
      setActiveStep(0);
      
      // Create preview URL for PDF
      const url = URL.createObjectURL(pdfFiles[0]);
      setPdfPreviewUrl(url);
    } else {
      // Batch mode
      setBatchMode(true);
      setUploadedFiles(pdfFiles);
      setCurrentFileIndex(0);
      
      // Initialize batch results
      const results = new Map();
      pdfFiles.forEach(file => {
        results.set(file.name, {
          status: 'pending',
          data: null,
          error: null,
          pdfUrl: URL.createObjectURL(file),
        });
      });
      setBatchResults(results);
    }
  };
  
  // Cleanup preview URL when component unmounts or file changes
  useEffect(() => {
    return () => {
      if (pdfPreviewUrl) {
        URL.revokeObjectURL(pdfPreviewUrl);
      }
    };
  }, [pdfPreviewUrl]);

  // Handle drag events
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files);
    }
  }, []);

  // Handle file input change
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileSelect(e.target.files);
    }
  };

  // Timer for processing time display
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    
    if (aiProcessing.isProcessing && activeStep === 1) {
      setProcessingTime(0);
      interval = setInterval(() => {
        setProcessingTime((prev) => prev + 1);
      }, 1000);
    } else {
      setProcessingTime(0);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [aiProcessing.isProcessing, activeStep]);

  // Start AI processing (single file)
  const handleStartProcessing = async () => {
    if (!uploadedFile) return;

    setActiveStep(1);
    setProcessingTime(0);
    try {
      await dispatch(processInvoiceWithAI(uploadedFile)).unwrap();
      // Step will be updated automatically by useEffect when extractedData changes
    } catch (error) {
      console.error('AI Processing failed:', error);
      setActiveStep(0);
    }
  };

  // Start batch processing (multiple files)
  const handleStartBatchProcessing = async () => {
    if (uploadedFiles.length === 0) return;

    // Don't change active step during batch - keep showing file list
    const newResults = new Map(batchResults);
    
    console.log(`🔄 Starting batch processing of ${uploadedFiles.length} files...`);

    // Process each file sequentially
    for (let i = 0; i < uploadedFiles.length; i++) {
      const file = uploadedFiles[i];
      setCurrentFileIndex(i);

      console.log(`📄 Processing file ${i + 1}/${uploadedFiles.length}: ${file.name}`);

      // Update status to processing
      newResults.set(file.name, {
        ...newResults.get(file.name)!,
        status: 'processing',
      });
      setBatchResults(new Map(newResults));

      try {
        // Process the file
        const result = await dispatch(processInvoiceWithAI(file)).unwrap();
        
        console.log(`✅ Successfully processed: ${file.name}`);
        
        // Update status to completed
        newResults.set(file.name, {
          status: 'completed',
          data: result.extractedData || null,
          error: null,
          pdfUrl: newResults.get(file.name)!.pdfUrl,
        });
        setBatchResults(new Map(newResults));
        
        // Clear the extracted data from Redux to prevent UI confusion
        dispatch(clearAIProcessing());
      } catch (error: any) {
        console.error(`❌ Failed to process ${file.name}:`, error);
        
        // Update status to error
        newResults.set(file.name, {
          ...newResults.get(file.name)!,
          status: 'error',
          error: error.message || 'Processing failed',
        });
        setBatchResults(new Map(newResults));
      }
    }

    // All files processed - move to review step
    const successCount = Array.from(newResults.values()).filter(r => r.status === 'completed').length;
    const failCount = uploadedFiles.length - successCount;
    
    console.log(`🎉 Batch processing complete: ${successCount} succeeded, ${failCount} failed`);
    
    if (successCount > 0) {
      // Move to batch review step
      setActiveStep(2);
    } else {
      // All failed - show error
      alert(`Všechny soubory selhaly při zpracování. Zkontrolujte prosím soubory a zkuste to znovu.`);
    }
  };

  // Handle field changes
  const handleFieldChange = (field: keyof InvoiceAIData, value: any) => {
    if (editedData) {
      setEditedData({
        ...editedData,
        [field]: value,
      });
    }
  };

  // Handle item changes
  const handleItemChange = (index: number, field: keyof InvoiceAIItem, value: any) => {
    if (editedData && editedData.items) {
      const newItems = [...editedData.items];
      newItems[index] = {
        ...newItems[index],
        [field]: value,
      };
      setEditedData({
        ...editedData,
        items: newItems,
      });
    }
  };

  // Create invoice
  const handleCreateInvoice = async () => {
    if (!editedData || !uploadedFile) return;

    setActiveStep(3);
    try {
      await dispatch(createInvoiceFromAI({ invoiceData: editedData, pdfFile: uploadedFile })).unwrap();
      
      // Success
      if (onSuccess) {
        onSuccess();
      }
      handleClose();
    } catch (error) {
      console.error('Invoice creation failed:', error);
      setActiveStep(2);
    }
  };

  // Close and reset
  const handleClose = () => {
    // Clean up single file mode
    if (pdfPreviewUrl) {
      URL.revokeObjectURL(pdfPreviewUrl);
    }
    
    // Clean up batch mode
    batchResults.forEach(result => {
      if (result.pdfUrl) {
        URL.revokeObjectURL(result.pdfUrl);
      }
    });
    
    setUploadedFile(null);
    setUploadedFiles([]);
    setBatchMode(false);
    setBatchResults(new Map());
    setCurrentFileIndex(0);
    setEditedData(null);
    setActiveStep(0);
    setPdfPreviewUrl(null);
    dispatch(clearAIProcessing());
    onClose();
  };

  // Render upload area
  const renderUploadArea = () => (
    <Box>
      <Paper
        sx={{
          p: 4,
          border: dragActive ? '2px dashed #1976d2' : '2px dashed #ccc',
          backgroundColor: dragActive ? '#f0f7ff' : '#fafafa',
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 0.3s',
        }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => document.getElementById('pdf-file-input')?.click()}
      >
        <UploadIcon sx={{ fontSize: 64, color: '#1976d2', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Nahrát Fakturu PDF
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Přetáhněte PDF soubor(y) sem, nebo klikněte pro výběr
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={{ mt: 1 }}>
          💡 Můžete vybrat více souborů najednou pro hromadné zpracování
        </Typography>
        <input
          id="pdf-file-input"
          type="file"
          accept="application/pdf"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileInputChange}
        />
      </Paper>

      {/* Single file selected */}
      {uploadedFile && !batchMode && (
        <Box sx={{ mt: 3 }}>
          <Alert severity="success" icon={<PdfIcon />}>
            <Typography variant="body2">
              <strong>Soubor vybrán:</strong> {uploadedFile.name} ({(uploadedFile.size / 1024 / 1024).toFixed(2)} MB)
            </Typography>
          </Alert>
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<AIIcon />}
              onClick={handleStartProcessing}
            >
              Zpracovat s AI
            </Button>
          </Box>
        </Box>
      )}

      {/* Multiple files selected (Batch mode) */}
      {batchMode && uploadedFiles.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Alert severity="info" icon={<PdfIcon />}>
            <Typography variant="body2">
              <strong>Hromadné nahrání:</strong> {uploadedFiles.length} souborů vybráno
            </Typography>
          </Alert>
          
          <Paper elevation={2} sx={{ mt: 2, p: 2, maxHeight: 300, overflowY: 'auto' }}>
            {uploadedFiles.map((file, index) => {
              const result = batchResults.get(file.name);
              return (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 1.5,
                    mb: 1,
                    borderRadius: 1,
                    backgroundColor: result?.status === 'completed' ? 'success.light' :
                                   result?.status === 'error' ? 'error.light' :
                                   result?.status === 'processing' ? 'primary.light' : 'grey.100',
                  }}
                >
                  <Box sx={{ mr: 2, fontSize: '1.5rem' }}>
                    {result?.status === 'completed' ? '✅' :
                     result?.status === 'error' ? '❌' :
                     result?.status === 'processing' ? '⏳' : '⏸️'}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {file.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                      {result?.error && ` - ${result.error}`}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Paper>

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<AIIcon />}
              onClick={handleStartBatchProcessing}
              disabled={aiProcessing.isProcessing}
            >
              Zpracovat Všechny s AI
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );

  // Render processing status
  const renderProcessingStatus = () => {
    // Calculate estimated progress based on time (simulated)
    const estimatedProgress = Math.min(95, Math.floor((processingTime / 15) * 100));
    
    // Generate status message based on elapsed time
    const getStatusMessage = () => {
      if (processingTime < 2) return '⏳ Nahrávám PDF na server...';
      if (processingTime < 4) return '📸 Mistral OCR rozpoznává text...';
      if (processingTime < 8) return '📄 OCR čte dokument...';
      if (processingTime < 12) return '🤖 AI strukturuje data...';
      if (processingTime < 15) return '✅ AI validuje a kontroluje...';
      return '⏳ Dokončuji zpracování...';
    };

    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress size={80} sx={{ mb: 3 }} />
        <Typography variant="h6" gutterBottom>
          AI Zpracovává Vaši Fakturu...
        </Typography>
        <Typography variant="body2" color="primary" sx={{ mb: 2 }}>
          {getStatusMessage()}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Čas zpracování: {processingTime}s
        </Typography>
        
        {/* Show status history if available */}
        {aiProcessing.statusHistory.length > 0 ? (
          <Box sx={{ mt: 3, px: 2 }}>
            <Paper elevation={2} sx={{ p: 2, backgroundColor: 'grey.50', maxHeight: 300, overflowY: 'auto' }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                Detailní průběh:
              </Typography>
              {aiProcessing.statusHistory.map((status, index) => (
                <Box 
                  key={index} 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start',
                    mb: 1.5,
                    pb: 1.5,
                    borderBottom: index < aiProcessing.statusHistory.length - 1 ? '1px solid #e0e0e0' : 'none'
                  }}
                >
                  <Box sx={{ mr: 1, fontSize: '1rem' }}>
                    {status.status === 'completed' ? '✅' : 
                     status.status === 'error' ? '❌' : 
                     status.status === 'in_progress' ? '⏳' : '⏸️'}
                  </Box>
                  <Box sx={{ flex: 1, textAlign: 'left' }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: status.status === 'in_progress' ? 600 : 400,
                        color: status.status === 'completed' ? 'success.main' : 
                               status.status === 'error' ? 'error.main' : 
                               status.status === 'in_progress' ? 'primary.main' : 'text.secondary'
                      }}
                    >
                      {status.message}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      {new Date(status.timestamp).toLocaleTimeString('cs-CZ')}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Paper>
          </Box>
        ) : (
          <Alert severity="info" sx={{ mt: 3, mx: 2 }} icon={<AIIcon />}>
            <Typography variant="caption">
              Zpracování faktury: Mistral OCR → AI. Typicky trvá 5-15 sekund.
            </Typography>
          </Alert>
        )}

        <LinearProgress 
          variant="determinate" 
          value={aiProcessing.statusHistory.length > 0 ? aiProcessing.progress : estimatedProgress} 
          sx={{ 
            mt: 3, 
            height: 8, 
            borderRadius: 4,
            mx: 2,
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
            }
          }} 
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {aiProcessing.statusHistory.length > 0 
            ? `${Math.round(aiProcessing.progress)}%` 
            : `Odhadovaný průběh: ${estimatedProgress}%`}
        </Typography>
      </Box>
    );
  };

  // Render review form
  const renderReviewForm = () => {
    if (!editedData) return null;

    return (
      <Box>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Zkontrolujte extrahovaná data níže. Porovnejte s PDF vlevo a před vytvořením faktury můžete upravit libovolné pole.
          </Typography>
        </Alert>

        <Grid container spacing={3}>
          {/* PDF Preview Column */}
          {pdfPreviewUrl && (
            <Grid item xs={12} md={6} lg={6}>
              <Paper
                elevation={3}
                sx={{
                  position: 'sticky',
                  top: 20,
                  height: '80vh',
                  overflow: 'hidden',
                  borderRadius: 2,
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: 'primary.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <PdfIcon />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Originální PDF
                  </Typography>
                </Box>
                <Box sx={{ height: 'calc(100% - 56px)', overflow: 'auto' }}>
                  <iframe
                    src={pdfPreviewUrl}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                    }}
                    title="PDF Preview"
                  />
                </Box>
              </Paper>
            </Grid>
          )}

          {/* Form Column */}
          <Grid item xs={12} md={pdfPreviewUrl ? 6 : 12} lg={pdfPreviewUrl ? 6 : 12}>
            <Grid container spacing={3}>
          {/* Invoice Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Informace o Faktuře
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Číslo Faktury"
              value={editedData.invoiceNumber || ''}
              onChange={(e) => handleFieldChange('invoiceNumber', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Datum Vystavení"
              type="date"
              value={editedData.invoiceDate || ''}
              onChange={(e) => handleFieldChange('invoiceDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Datum Splatnosti"
              type="date"
              value={editedData.dueDate || ''}
              onChange={(e) => handleFieldChange('dueDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Supplier Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Informace o Dodavateli
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Název Dodavatele"
              value={editedData.supplierName || ''}
              onChange={(e) => handleFieldChange('supplierName', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Adresa Dodavatele"
              value={editedData.supplierAddress || ''}
              onChange={(e) => handleFieldChange('supplierAddress', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="IČO Dodavatele"
              value={editedData.supplierICO || ''}
              onChange={(e) => handleFieldChange('supplierICO', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="DIČ Dodavatele"
              value={editedData.supplierDIC || ''}
              onChange={(e) => handleFieldChange('supplierDIC', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Bankovní Účet"
              value={editedData.supplierBankAccount || ''}
              onChange={(e) => handleFieldChange('supplierBankAccount', e.target.value)}
              helperText={
                editedData.matchedCustomerName
                  ? `Dodavatel v adresáři: ${editedData.matchedCustomerName}`
                  : editedData.matchedCustomerId
                    ? 'Dodavatel nalezen v adresáři'
                    : undefined
              }
            />
            <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
              {(() => {
                const normalize = (s?: string | null) => (s || '').trim().replace(/\s+/g, '');
                const fromDirectory =
                  !!editedData.directoryBankAccount &&
                  normalize(editedData.supplierBankAccount) === normalize(editedData.directoryBankAccount);
                return (
                  <Chip
                    size="small"
                    color={fromDirectory ? 'success' : 'default'}
                    label={fromDirectory ? 'Účet z adresáře' : 'Účet z faktury'}
                  />
                );
              })()}
              {editedData.ocrSupplierBankAccount &&
                editedData.directoryBankAccount &&
                (editedData.ocrSupplierBankAccount || '').trim().replace(/\s+/g, '') !==
                  (editedData.directoryBankAccount || '').trim().replace(/\s+/g, '') && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`OCR: ${editedData.ocrSupplierBankAccount}`}
                />
              )}
            </Stack>
          </Grid>

          {/* Customer Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Informace o Odběrateli
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Název Odběratele"
              value={editedData.customerName || ''}
              onChange={(e) => handleFieldChange('customerName', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Adresa Odběratele"
              value={editedData.customerAddress || ''}
              onChange={(e) => handleFieldChange('customerAddress', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="IČO Odběratele"
              value={editedData.customerICO || ''}
              onChange={(e) => handleFieldChange('customerICO', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="DIČ Odběratele"
              value={editedData.customerDIC || ''}
              onChange={(e) => handleFieldChange('customerDIC', e.target.value)}
            />
          </Grid>

          {/* Financial Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Finanční Informace
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Základ Daně"
              value={editedData.subtotal || ''}
              onChange={(e) => handleFieldChange('subtotal', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="DPH"
              value={editedData.vatAmount || ''}
              onChange={(e) => handleFieldChange('vatAmount', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Celkem"
              value={editedData.totalAmount || ''}
              onChange={(e) => handleFieldChange('totalAmount', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Měna"
              value={editedData.currency || 'CZK'}
              onChange={(e) => handleFieldChange('currency', e.target.value)}
            />
          </Grid>

          {/* Invoice Items */}
          {editedData.items && editedData.items.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Položky Faktury ({editedData.items.length})
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Popis</TableCell>
                      <TableCell align="right">Množství</TableCell>
                      <TableCell align="right">Jedn. Cena</TableCell>
                      <TableCell align="right">Sazba DPH (%)</TableCell>
                      <TableCell align="right">Cena Celkem</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {editedData.items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            value={item.description || ''}
                            onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={item.quantity || ''}
                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                            sx={{ width: 80 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={item.unitPrice || ''}
                            onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                            sx={{ width: 100 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={item.vatRate || ''}
                            onChange={(e) => handleItemChange(index, 'vatRate', e.target.value)}
                            sx={{ width: 80 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={item.totalPrice || ''}
                            onChange={(e) => handleItemChange(index, 'totalPrice', e.target.value)}
                            sx={{ width: 100 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          )}

          {/* Suggested Business Unit */}
          {editedData.suggestedBusinessUnitName && (
            <Grid item xs={12}>
              <Alert severity="success" icon={<CheckIcon />}>
                <Typography variant="body2">
                  <strong>Navržená Účetní Jednotka:</strong> {editedData.suggestedBusinessUnitName}
                </Typography>
              </Alert>
            </Grid>
          )}

          {/* Notes */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Poznámky"
              value={editedData.notes || ''}
              onChange={(e) => handleFieldChange('notes', e.target.value)}
            />
          </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    );
  };

  // Render batch review (for multiple processed files)
  const renderBatchReview = () => {
    const completedFiles = uploadedFiles.filter(file => {
      const result = batchResults.get(file.name);
      return result?.status === 'completed' && result.data;
    });

    if (completedFiles.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Alert severity="error">
            <Typography variant="body2">
              Žádné faktury nebyly úspěšně zpracovány. Zkuste to prosím znovu.
            </Typography>
          </Alert>
        </Box>
      );
    }

    return (
      <Box>
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Zpracováno:</strong> {completedFiles.length} z {uploadedFiles.length} souborů úspěšně.
            {completedFiles.length < uploadedFiles.length && (
              <span> ({uploadedFiles.length - completedFiles.length} selhalo)</span>
            )}
          </Typography>
        </Alert>

        <Typography variant="h6" gutterBottom>
          Extrahované Faktury
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Klikněte na fakturu pro vytvoření v systému.
        </Typography>

        <Grid container spacing={2}>
          {completedFiles.map((file, index) => {
            const result = batchResults.get(file.name);
            const data = result?.data;
            if (!data) return null;

            return (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Card 
                  elevation={3}
                  sx={{ 
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 6,
                    }
                  }}
                  onClick={async () => {
                    // Create this invoice
                    try {
                      await dispatch(createInvoiceFromAI({ 
                        invoiceData: data, 
                        pdfFile: file 
                      })).unwrap();
                      
                      alert(`✅ Faktura ${data.invoiceNumber || file.name} vytvořena!`);
                      
                      // Remove from batch results
                      const newResults = new Map(batchResults);
                      newResults.delete(file.name);
                      setBatchResults(newResults);
                      
                      // If all invoices created, close dialog
                      if (newResults.size === 0 || Array.from(newResults.values()).every(r => r.status !== 'completed')) {
                        if (onSuccess) onSuccess();
                        handleClose();
                      }
                    } catch (error) {
                      console.error('Failed to create invoice:', error);
                      alert(`❌ Chyba při vytváření faktury: ${error}`);
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                      <PdfIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {data.invoiceNumber || 'Bez čísla'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {file.name}
                        </Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 1.5 }} />

                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Dodavatel:</strong>
                      </Typography>
                      <Typography variant="body2">
                        {data.supplierName || 'N/A'}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Odběratel:</strong>
                      </Typography>
                      <Typography variant="body2">
                        {data.customerName || 'N/A'}
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 1.5 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Datum vystavení:
                      </Typography>
                      <Typography variant="body2">
                        {data.invoiceDate || 'N/A'}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Splatnost:
                      </Typography>
                      <Typography variant="body2">
                        {data.dueDate || 'N/A'}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Celkem:
                      </Typography>
                      <Typography variant="h6" color="primary.main">
                        {data.totalAmount || '0'} {data.currency || 'CZK'}
                      </Typography>
                    </Box>

                    <Button
                      variant="contained"
                      fullWidth
                      size="small"
                      startIcon={<CheckIcon />}
                      sx={{ mt: 1 }}
                    >
                      Vytvořit Fakturu
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Show failed files if any */}
        {uploadedFiles.filter(f => batchResults.get(f.name)?.status === 'error').length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Alert severity="warning">
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Neúspěšně zpracované soubory:</strong>
              </Typography>
              {uploadedFiles
                .filter(f => batchResults.get(f.name)?.status === 'error')
                .map((file, idx) => (
                  <Typography key={idx} variant="caption" display="block">
                    ❌ {file.name} - {batchResults.get(file.name)?.error}
                  </Typography>
                ))}
            </Alert>
          </Box>
        )}
      </Box>
    );
  };

  // Render creating status
  const renderCreatingStatus = () => (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <CircularProgress size={60} sx={{ mb: 2 }} />
      <Typography variant="h6">Vytváření Faktury...</Typography>
    </Box>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AIIcon sx={{ mr: 1 }} />
            Nahrání Faktury s AI
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {aiProcessing.error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {aiProcessing.error}
          </Alert>
        )}

        {activeStep === 0 && renderUploadArea()}
        {activeStep === 1 && renderProcessingStatus()}
        {activeStep === 2 && (batchMode ? renderBatchReview() : renderReviewForm())}
        {activeStep === 3 && renderCreatingStatus()}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={aiProcessing.isProcessing || aiProcessing.isCreating}>
          Zrušit
        </Button>
        {activeStep === 2 && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateInvoice}
            disabled={!editedData || aiProcessing.isCreating}
            startIcon={<CheckIcon />}
          >
            Vytvořit Fakturu
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AIInvoiceUpload;

